import React, { useState } from 'react';
import styled from 'styled-components';
import Logo from '../../../images/logoAntsNest.png';
import AdminDashboard from '../AdminDashboard';
import TeamMenu from '../TeamMenu';
import TeamModal from '../TeamModal';
import TeamTreeManager from '../TeamTreeManager';
import { useAuth } from '../../../contexts/AuthContext';
import { useRecoilState, useRecoilValue } from 'recoil';
import { ThemeAtomState } from '../../../recoil/ModalAtom';
import { ActiveTeamAtom } from '../../recoil/teamAtoms';

const HeaderContainer = styled.div`
  width: 100%;
  position: sticky;
  top: 0;
  z-index: 50;
`;

const Header = styled.div`
  background-color: ${p => p.theme.bgHeader};
  border-bottom: 1px solid ${p => p.theme.border};
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 72px;
  box-sizing: border-box;
  padding: 0 24px;
  color: ${p => p.theme.text};
  transition: ${p => p.theme.transition};

  @media screen and (max-width: 768px) {
    height: 64px;
    padding: 0 12px;
  }
  @media screen and (max-width: 380px) {
    padding: 0 6px;
  }
`;

const LogoDiv = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  flex: 1;

  @media screen and (max-width: 768px) {
    gap: 8px;
  }
`;

const LogoImg = styled.img`
  height: 36px;
  width: auto;
  flex-shrink: 0;

  @media screen and (max-width: 768px) {
    height: 28px;
  }
  @media screen and (max-width: 380px) {
    height: 22px;
  }
`;

const Title = styled.h1`
  margin: 0;
  padding-left: 12px;
  border-left: 1px solid ${p => p.theme.border};
  font-size: 16px;
  font-weight: 700;
  color: ${p => p.theme.text};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media screen and (max-width: 768px) {
    font-size: 13px;
    padding-left: 8px;
  }
  @media screen and (max-width: 480px) {
    display: none;
  }
`;

const RightArea = styled.div`
  display: flex;
  gap: 6px;
  align-items: center;
  flex-shrink: 0;

  @media screen and (max-width: 768px) {
    gap: 2px;
  }
`;

const UserName = styled.span`
  color: ${p => p.theme.textSecondary};
  font-size: 13px;
  margin-right: 8px;
  white-space: nowrap;

  @media screen and (max-width: 768px) {
    display: none;
  }
`;

const IconButton = styled.button`
  background: transparent;
  border: none;
  color: ${p => p.theme.textSecondary};
  width: 36px;
  height: 36px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  transition: ${p => p.theme.transition};

  &:hover {
    background: ${p => p.theme.bgHover};
    color: ${p => p.theme.text};
  }

  svg {
    width: 18px;
    height: 18px;
    stroke: currentColor;
    fill: none;
    stroke-width: 1.8;
    stroke-linecap: round;
    stroke-linejoin: round;
  }

  @media screen and (max-width: 380px) {
    width: 30px;
    height: 30px;
    svg { width: 16px; height: 16px; }
  }
`;

const NotificationDot = styled.span`
  position: absolute;
  top: 6px;
  right: 6px;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: ${p => p.theme.error};
  border: 2px solid ${p => p.theme.bgHeader};
`;

const IconMoon = () => (
  <svg viewBox="0 0 24 24"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" /></svg>
);
const IconSun = () => (
  <svg viewBox="0 0 24 24">
    <circle cx="12" cy="12" r="4" />
    <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
  </svg>
);
const IconBell = () => (
  <svg viewBox="0 0 24 24">
    <path d="M18 8a6 6 0 1 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
  </svg>
);
const IconGear = () => (
  <svg viewBox="0 0 24 24">
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33h0a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82v0a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
  </svg>
);
const IconTree = () => (
  <svg viewBox="0 0 24 24">
    <rect x="3" y="3" width="7" height="7" rx="1" />
    <rect x="14" y="3" width="7" height="7" rx="1" />
    <rect x="3" y="14" width="7" height="7" rx="1" />
    <rect x="14" y="14" width="7" height="7" rx="1" />
  </svg>
);

const LogoutButton = styled.button`
  background: transparent;
  border: 1px solid ${p => p.theme.border};
  color: ${p => p.theme.textSecondary};
  padding: 8px 14px;
  border-radius: ${p => p.theme.radiusSm};
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: ${p => p.theme.transition};
  margin-left: 6px;
  white-space: nowrap;

  &:hover {
    background: ${p => p.theme.accent};
    color: white;
    border-color: ${p => p.theme.accent};
  }

  @media screen and (max-width: 768px) {
    padding: 6px 10px;
    font-size: 12px;
    margin-left: 0;
  }
  @media screen and (max-width: 380px) {
    padding: 5px 7px;
    font-size: 11px;
  }
`;

function AppHeader() {
  const { user, logout } = useAuth();
  const [showAdminDashboard, setShowAdminDashboard] = useState(false);
  const [showTeamTreeManager, setShowTeamTreeManager] = useState(false);
  const [themeMode, setThemeMode] = useRecoilState(ThemeAtomState);
  const activeTeam = useRecoilValue(ActiveTeamAtom);

  const toggleTheme = () => {
    const next = themeMode === 'light' ? 'dark' : 'light';
    setThemeMode(next);
    localStorage.setItem('estimate-theme', next);
  };

  // 활성팀이 있으면 팀 트리 관리자, 없으면 시스템 AdminDashboard
  const toggleTreeManager = () => {
    if (activeTeam) {
      setShowTeamTreeManager(prev => !prev);
    } else {
      setShowAdminDashboard(prev => !prev);
    }
  };

  const handleLogout = async () => {
    await logout();
  };

  return (
    <>
      <HeaderContainer>
        <Header>
          <LogoDiv>
            <LogoImg src={Logo} alt="AntsNest Logo" />
            <Title>견적서 시스템</Title>
          </LogoDiv>
          <RightArea>
            <TeamMenu />
            <UserName>{user?.userName || user?.user_name || user?.userId || user?.user_id || '사용자'}님</UserName>
            <IconButton onClick={toggleTheme} title={themeMode === 'light' ? '다크 모드' : '라이트 모드'}>
              {themeMode === 'light' ? <IconMoon /> : <IconSun />}
            </IconButton>
            <IconButton
              onClick={toggleTreeManager}
              title={activeTeam ? `팀 트리 관리 (${activeTeam.name})` : '시스템 트리 관리'}
            ><IconTree /></IconButton>
            <LogoutButton onClick={handleLogout}>로그아웃</LogoutButton>
          </RightArea>
        </Header>
      </HeaderContainer>

      <AdminDashboard
        isOpen={showAdminDashboard}
        onClose={() => setShowAdminDashboard(false)}
      />

      <TeamTreeManager
        isOpen={showTeamTreeManager}
        team={activeTeam}
        onClose={() => setShowTeamTreeManager(false)}
      />

      <TeamModal />
    </>
  );
}

export default AppHeader;
